import { Circle, Control, DivIcon, DomUtil, LatLngBounds, Marker, Point, Polygon } from 'leaflet';
import { ClusterLayerOptions, MarkerClusterGroup, NativeClusterLayer } from './native-cluster-layer';
import { digger } from '@cbre/digger';
import { abbrevNumber } from '@miq/utils';
import { LOCALE_MAP_CLIENT_MAP, LocaleMapId, MapClientKey } from '@maps/maps.models';
import { DataObject } from '@shared/models/data-object.model';
import { EntityType } from '@shared/models/entity-enum';
import { ADD, EMPTY_STRING } from '@shared/models/strings.constants';
import { UsageTypes } from '@core/composers/usages/usage';
import { isNotNull } from '@shared/functions';
import { MarkersInfo } from '../records-details/visual-widget/map-widget/map-widget.model';
import {
  EntityInfoColloquialModel,
  GeoPolygon,
  GeoPolygonType,
  LatLong,
  LeafletPosition,
  LeafletShape,
} from './esri-map.model';
import { MapIcon, PinPadding, svgIconMaker } from './icon-markers.service';
import { HtmlAttrsNames, HtmlTagNames } from './card-info/html-elements';

/** SERVICES  */
export const getViewPortPolygon = bounds => {
  const { _northEast, _southWest } = bounds;
  const { lat: northLat, lng: eastLong } = _northEast;
  const { lat: southLat, lng: westLong } = _southWest;
  const _northWest = { lat: northLat, long: westLong };
  const _southEast = { lat: southLat, long: eastLong };
  return [_northWest, { lat: northLat, long: eastLong }, _southEast, { lat: southLat, long: westLong }, _northWest];
};

export const makeGeoPolygon: (latLongs) => GeoPolygon = latlongs => ({
  type: GeoPolygonType.geoPolygon,
  location: latlongs,
});

export const getPolygonLatLongs: (layer) => GeoPolygon = layer => {
  const { 0: latlongs } = layer.getLatLngs();
  const { 0: first } = latlongs;
  return makeGeoPolygon([...latlongs, first].map(({ lat, lng }) => ({ lat, long: lng })));
};

export const getCircleGeoPolygon: (layer) => GeoPolygon = layer => {
  const { lat, lng: long } = layer.getLatLng();
  const distance = layer.getRadius();
  return {
    type: GeoPolygonType.geoDistance,
    location: { lat, long } as any as LatLong,
    distance: `${Math.round(distance)}m`,
  };
};

export const makeShapePolygon = mapState => {
  const { shape, layer } = mapState.shape;
  return shape === LeafletShape.Polygon ? getPolygonLatLongs(layer) : getCircleGeoPolygon(layer);
};

export const makeSearchAsMoveControl = ({ onClick, value = false, localeText }) => {
  const checkBoxControl = Control.extend({
    options: {
      position: LeafletPosition.TopRight,
    },

    onAdd: () => {
      const checkboxId = 'markAsMoveCheckbox';
      const label = DomUtil.create(HtmlTagNames.Label, 'container');
      label.setAttribute(HtmlAttrsNames.For, checkboxId);
      const text = DomUtil.create(HtmlTagNames.Span, 'checkmark-label');
      text.innerText = localeText;
      const parentSpan = DomUtil.create(HtmlTagNames.Span, 'checkmark');
      parentSpan.appendChild(text);
      const checkbox = DomUtil.create(HtmlTagNames.Input, 'search-as-move-checkbox');
      checkbox.type = HtmlTagNames.Checkbox;
      checkbox.id = checkboxId;
      checkbox.value = String(value);
      checkbox.checked = value;
      checkbox.onchange = e => onClick(e);
      const wrapper = DomUtil.create(HtmlTagNames.Div, 'miq-search-as-move');
      label.appendChild(checkbox);
      label.appendChild(parentSpan);
      wrapper.appendChild(label);
      return wrapper;
    },
  });
  return new checkBoxControl();
};

export const makeInfoIconControl = translateText => {
  const infoBoxControl = Control.extend({
    options: { position: LeafletPosition.TopRight },

    onAdd: () => {
      const message = DomUtil.create(HtmlTagNames.Div, 'miq-warning-msg');
      message.setAttribute(HtmlAttrsNames.InnerHTML, translateText);

      const imgTag = DomUtil.create(HtmlTagNames.Img);
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/mapinfo-icon.svg');
      imgTag.setAttribute(HtmlAttrsNames.Alt, translateText);
      const wrapper = DomUtil.create(HtmlTagNames.Div, 'max-limit-reached');
      wrapper.onmouseenter = () => {
        message.className = 'miq-warning-msg miq-warning-msg-show';
      };
      wrapper.onmouseleave = () => {
        message.className = 'miq-warning-msg miq-warning-msg-hide';
      };
      wrapper.appendChild(message);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new infoBoxControl();
};

export const makeEditIconControl = (translateText, onClick) => {
  const editBoxControl = Control.extend({
    options: {
      position: LeafletPosition.TopLeft,
    },

    onAdd: () => {
      const imgTag = DomUtil.create(HtmlTagNames.Img, 'edit-marker-icon');
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/edit-icon.svg');
      imgTag.setAttribute(HtmlAttrsNames.Alt, translateText);
      imgTag.onclick = e => onClick(e);
      const wrapper = DomUtil.create(HtmlTagNames.Div);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new editBoxControl();
};

export const makeStreetViewControl = onClick => {
  const streetViewControl = Control.extend({
    options: {
      position: LeafletPosition.BottomRight,
    },

    onAdd: () => {
      const imgTag = DomUtil.create(HtmlTagNames.Img, 'street-view-icon');
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/street-view.svg');
      imgTag.onclick = onClick;
      const wrapper = DomUtil.create(HtmlTagNames.Div);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new streetViewControl();
};

export const makeClearPolygonControl = (onClick, localeText) => {
  const checkBoxControl = Control.extend({
    options: {
      position: LeafletPosition.TopLeft,
    },

    onAdd: () => {
      const clearButton = DomUtil.create(HtmlTagNames.Button, 'btn-light btn-cancel-outlined');
      clearButton.innerText = localeText;
      clearButton.onclick = e => onClick(e);
      return clearButton;
    },
  });
  return new checkBoxControl();
};

export const makeGroupingLayer = (markers: Marker[], options: ClusterLayerOptions = {}): NativeClusterLayer => {
  const group = new MarkerClusterGroup(options);
  group.addLayers(markers);
  return group;
};

export const makeClickToAddPinControl = (onClick, localeText) => {
  const clickToAddPinControl = Control.extend({
    options: {
      position: LeafletPosition.TopRight,
    },

    onAdd: () => {
      const clickToAddButton = DomUtil.create(HtmlTagNames.Button, 'click-to-add-pin');
      clickToAddButton.innerText = localeText;
      clickToAddButton.onclick = e => onClick(e);
      return clickToAddButton;
    },
  });
  return new clickToAddPinControl();
};

/** MAP AND BUILDS THE MARKERS */
export const buildMarkers = (
  markers,
  hoveredRow,
  isDraggable?: boolean,
  shadow?: boolean,
  esriMap?: { [markerId: string]: number },
  iconToUse?: MapIcon
) => {
  return markers.map((marker, index) => {
    const [id, latitude, longitude, pending, value, isHeadQuarter] = marker;
    const isShadow = shadow ? shadow : false;
    const hovered = id === hoveredRow;
    const temp = value === ADD ? value : EMPTY_STRING;
    const paddingLeft = getPaddingValue(value);
    const iconOpts = {
      text: !!+value ? abbrevNumber(value) : temp,
      pending: !!pending,
      hovered,
      id,
      isHeadQuarter,
      isShadow,
      paddingLeft,
      iconToUse,
    };
    const icon = svgIconMaker(iconOpts, value == null || value === EMPTY_STRING ? { width: 30, height: 40 } : null);
    const markerValue = new Marker([latitude, longitude], {
      icon,
      id,
      value: value != null ? value : EMPTY_STRING,
      pending,
      hovered,
      pmIgnore: true,
      isHeadQuarter,
      draggable: isDraggable ? isDraggable : false,
    } as any);
    if (!!esriMap) {
      const markerId = `${id}.${latitude}.${longitude}`;
      esriMap[markerId] = index;
    }
    return markerValue;
  });
};

/**
 * Returns true if given marker contract is valid and contains lat and long
 * @param param0
 * @returns
 */
const hasValidMarkerContract = ([_, latitude, longitude]) => isNotNull(latitude, longitude);

/**
 * SINCE COMPANY IS GIVING LOCATIONS AS NESTED, IT NEEDS SPECIAL TREATMENT
 * SO WE ADDED THIS FUNCTION (ONLY FOR COMPANY)
 */
const toCompanyMarkers = markers =>
  markers
    .filter(([_id, locations]) => locations?.length > 0)
    .map(([id, locations, pending]) => {
      return ((Array.isArray(locations) && locations) || [])
        .filter(hasValidMarkerContract)
        .map(([isHeadQuarter, latitude, longitude]) => [id, latitude, longitude, pending, EMPTY_STRING, isHeadQuarter]);
    });

/**
 *  Returns primary lat long unless is retail & alternate lat long is available
 */
const toPrimaryUnlessRetailAltPresence = ([id, latitude, longitude, usageType, pending, altLat, altLong]) => {
  if (usageType === UsageTypes.Retail && (altLat !== 0 || altLong !== 0)) {
    return [id, altLat, altLong, pending, EMPTY_STRING];
  }
  return [id, latitude, longitude, pending, EMPTY_STRING];
};

/**
 * Uses primary lat long unless is retail & alternate lat long is available
 */
const toLeaseMarkers = markers => markers.filter(hasValidMarkerContract).map(toPrimaryUnlessRetailAltPresence);

/**
 * Uses primary lat long unless is retail & alternate lat long is available
 */
const toAvailabilityMarkers = markers => markers.filter(hasValidMarkerContract).map(toPrimaryUnlessRetailAltPresence);

/** ALL OTHER ENTITIES THAN AVAILABILITY OR PROERTY SHOULD BE EMPTY MARKERS SO WE SKIP THE VALUE. */
const toDefaultMarkers = markers =>
  markers
    .filter(hasValidMarkerContract)
    .map(([id, latitude, longitude, pending /** SKIP VALUE */, , isHeadQuarter]) => [
      id,
      latitude,
      longitude,
      pending,
      EMPTY_STRING,
      isHeadQuarter,
    ]);

const makeMarkersMap = {
  [EntityType.Company]: markers => toCompanyMarkers(markers).flat(1),
  [EntityType.LeaseComp]: toLeaseMarkers,
  [EntityType.Availability]: toAvailabilityMarkers,
};

/**
 * ONLY PROPERTY AND AVAILABILITY BUBBLES WILL HAVE VALUE INSIDE,
 * ALL OTHERS SHOULD FALL POSITIVE into shouldBeEmptyMarker
 */
export const makeMarkers = (
  { markers, hoveredRow, showOnlyPending = false, entity },
  esriMap?: { [markerId: string]: number }
) => {
  markers = showOnlyPending ? markers.filter(({ 3: pending }) => pending) : markers;
  // APPLY SPECIAL TREATMENT FOR COMPANY & CONTACT, ALL OTHERS (SALE, LEASE, SHOULD BE EMPTY ONLY)
  const markersMaker = makeMarkersMap[entity] ?? toDefaultMarkers;
  markers = markersMaker(markers);

  // To protect performance limit 5000 OR less (total)
  markers = markers.slice(0, 5000);
  return buildMarkers(markers, hoveredRow, false, false, esriMap);
};

export const updateMarkers = (
  markers,
  hoveredRow: { id: number; previous?: number },
  esriMap?: { [markerId: string]: number }
) => {
  const isMouseout = hoveredRow.previous != null;
  const id = isMouseout ? hoveredRow.previous : hoveredRow.id;
  return markers.map((marker, index) => {
    const { value, pending } = marker.options;
    const { lat, lng } = marker._latlng;
    const markerId = `${id}.${lat}.${lng}`;
    if (!!esriMap) {
      if (esriMap[markerId] != null && esriMap[markerId] === index) {
        setMarkerIcon(marker, value, pending, !isMouseout);
      }
    } else {
      setMarkerIcon(marker, value, pending, marker.options.id === hoveredRow.id);
    }
    return marker;
  });
};

function setMarkerIcon(marker: any, value: any, pending: any, hoveredCondition: boolean) {
  const iconOpts = {
    text: !!+value ? abbrevNumber(value) : EMPTY_STRING,
    pending,
    hovered: hoveredCondition,
  };
  const icon = svgIconMaker(iconOpts);
  marker.setIcon(icon);
}

function getPaddingValue(pinValue: number) {
  if (pinValue > 9 && pinValue < 100) {
    return PinPadding.Medium;
  } else if (pinValue > 99) {
    return PinPadding.Small;
  } else {
    return PinPadding.Large;
  }
}
export function getOffsetToCenter(zoom: number): number {
  let offsetToCenter = 1;
  if (zoom >= 17) {
    offsetToCenter = 0.0005;
  } else if (zoom >= 14) {
    offsetToCenter = 0.001;
  } else if (zoom >= 12) {
    offsetToCenter = 0.005;
  } else if (zoom >= 9) {
    offsetToCenter = 0.01;
  } else if (zoom >= 6) {
    offsetToCenter = 0.1;
  }
  return offsetToCenter;
}

export const makeLatLngBounds = ({ _northEast, _southWest }) => {
  return new LatLngBounds(_northEast, _southWest);
};

export const leafletifyShape = ({ shape, layer }) => {
  layer =
    shape === LeafletShape.Polygon
      ? new Polygon(layer._latlngs, layer.options)
      : new Circle(layer._latlng, { radius: layer._mRadius, ...layer.options });
  return { shape, layer };
};

export const groupMarkerIconMaker = cluster => {
  const pendingMarker = cluster
    .getAllChildMarkers()
    .map(({ options }) => options)
    .find(({ pending }) => !!pending);
  const pendingTag = !!pendingMarker ? `<span class="pending-group"></span>` : EMPTY_STRING;
  return new DivIcon({
    html: `<div><span>${cluster.getChildCount()}</span> ${pendingTag} </div>`,
    className: 'marker-cluster marker-cluster-medium',
    iconSize: new Point(40, 40),
  });
};

export const isGaodeGeocode = (userLocaleId: LocaleMapId, data: DataObject, path: string) => {
  const addlGeocodes = digger(data, path);
  return LOCALE_MAP_CLIENT_MAP[userLocaleId] === MapClientKey.Gaode && addlGeocodes != null && addlGeocodes.length
    ? addlGeocodes.find(
        geocodes => geocodes.geocode_source_provider_type === LOCALE_MAP_CLIENT_MAP[userLocaleId].toUpperCase()
      )
    : null;
};

export const getGaodeIndex = (userLocaleId: LocaleMapId, data, path: string) => {
  // considering gaode geo block exists in 'postal_address.addl_geocodes' array
  const addlGeocodes = digger(data, path);
  return addlGeocodes.findIndex(
    geocodes => geocodes.geocode_source_provider_type === LOCALE_MAP_CLIENT_MAP[userLocaleId].toUpperCase()
  );
};

type LocationWithHQ = DataObject & { headquarter_location_f?: boolean };

export const getDefaultLocation = (location: LocationWithHQ, postalAddressPath: string): MarkersInfo => {
  const { latitude, longitude } = digger(location, postalAddressPath) ?? {};
  return [latitude, longitude, false, EMPTY_STRING, location.headquarter_location_f];
};

/**
 *
 * @param colloquial
 * @param countryCodeDesc
 * @returns colloquial in case the country code coincides with that of the user
 */
const findCountryColloquial = (colloquial: DataObject[], countryCodeDesc: string): DataObject | null => {
  return colloquial
    .filter(({ country_code_desc }) => country_code_desc != null)
    .find(({ country_code_desc }) => countryCodeDesc === country_code_desc);
};

/**
 *
 * @param data
 * @param colloquial
 * @param countryCodeDesc
 * @param param
 * @returns colloquial or record search parameter value
 */
export function getColloquialParam(
  data: DataObject,
  colloquial: DataObject[],
  countryCodeDesc: string,
  param: string
): string | null {
  const countryColloquial = findCountryColloquial(colloquial, countryCodeDesc);
  return countryColloquial ? countryColloquial[param] : data[param];
}

// Configuration for colloquial search in case it does not come in the record
export const EntityInfoColloquial: EntityInfoColloquialModel = {
  [EntityType.Contact]: {
    entity: EntityType.Company,
    getId: (record: DataObject) => {
      const companies = digger(record, 'company_role_addresses');
      const company = companies.length > 0 ? companies[0] : null;
      return company?.company_id;
    },
  },
  [EntityType.Tenancy]: {
    entity: EntityType.Property,
    getId: (record: DataObject) => {
      return record.property_id;
    },
  },
};
